import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'

interface SignInFormProps {
  onSuccess?: () => void
  showLinks?: boolean
}

export function SignInForm({ onSuccess, showLinks = true }: SignInFormProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { signIn, federatedSignIn } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      await signIn(username, password)
      onSuccess?.()
    } catch (error) {
      toast.error('Failed to sign in. Please check your credentials.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSocialSignIn = async () => {
    try {
      await federatedSignIn('Google')
      // Redirect will happen automatically
    } catch (error: any) {
      console.error('Social sign-in error:', error)
      toast.error(
        `Failed to sign in with Google: ${error?.message || 'Unknown error'}`,
      )
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Input
            id="username"
            type="text"
            placeholder="Username or Email"
            value={username}
            className="rounded-xl shadow-xl"
            onChange={e => setUsername(e.target.value)}
            required
          />
        </div>
        <div className="space-y-2">
          <Input
            id="password"
            type="password"
            placeholder="Password"
            className="rounded-xl shadow-xl"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
        </div>
        <Button
          type="submit"
          className="w-full cursor-pointer rounded-xl shadow-xl mt-4"
          disabled={isLoading}
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </Button>
      </form>

      <div className="mt-4 space-y-2">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase py-4">
            <span className="bg-white dark:bg-slate-800 px-2 text-muted-foreground">
              Or continue with
            </span>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full cursor-pointer rounded-xl shadow-xl"
          onClick={handleSocialSignIn}
        >
          <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="currentColor"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="currentColor"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="currentColor"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          Continue with Google
        </Button>
      </div>

      {showLinks && (
        <div className="mt-6 text-center space-y-2">
          <Link
            to="/auth/forgot-password"
            className="text-xs text-blue-700 hover:underline dark:text-blue-400 block"
          >
            Forgot password?
          </Link>
          <div className="text-xs text-slate-600 dark:text-slate-400">
            Don't have an account?{' '}
            <Link
              to="/auth/signup"
              className="text-blue-700 hover:underline dark:text-blue-400"
            >
              Sign up
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
